import { Button } from '@components/common/Button';
import { FormCurrencyField } from '@components/common/FormCurrencyField/FormCurrencyField';
import { InputAdornment } from '@components/common/InputAdornment';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApplicationFormPageEnum } from '../types';
import { householdSpendingsQuestions } from './questions';
import { IHouseholdSpendingsForm } from './types';
import { getCurrentRoute } from '@utils/location';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { parseApplicationInProgress } from '../helpers';
import { useEffect } from 'react';

export const HouseholdSpendings = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IHouseholdSpendingsForm>({
    defaultValues: {
      essentialSpending: '',
      lifestyleSpending: '',
    },
  });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IHouseholdSpendingsForm) => {
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: householdSpendingsQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.HOUSEHOLD_OUTGOINGS);
    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div>
          <FormattedMessage id={householdSpendingsQuestions.essentialSpending.labelKey} tagName={'h4'} />
          <FormattedMessage id={householdSpendingsQuestions.essentialSpending.explanationKey} tagName={'p'} />
          <FormCurrencyField
            name={'essentialSpending'}
            rules={{ required: requiredFieldErrorMessage }}
            textFieldProps={{
              InputProps: {
                endAdornment: (
                  <InputAdornment position={'end'}>{intl.formatMessage({ id: 'common.monthly' })}</InputAdornment>
                ),
              },
              inputProps: { min: 0 },
            }}
          />
        </div>

        <div className={'mt-8'}>
          <FormattedMessage id={householdSpendingsQuestions.lifestyleSpending.labelKey} tagName={'h4'} />
          <FormattedMessage id={householdSpendingsQuestions.lifestyleSpending.explanationKey} tagName={'p'} />
          <FormCurrencyField
            name={'lifestyleSpending'}
            rules={{ required: requiredFieldErrorMessage }}
            textFieldProps={{
              InputProps: {
                endAdornment: (
                  <InputAdornment position={'end'}>{intl.formatMessage({ id: 'common.monthly' })}</InputAdornment>
                ),
              },
              inputProps: { min: 0 },
            }}
          />
        </div>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
